import Immutable from "immutable";
import React from "react";
import {
  Change,
  FormTemplateId,
  FormTemplateIdType,
  hasChanges,
} from "src/scenes/orgAdmin/formTemplates/types/common";
import * as Draft from "src/scenes/orgAdmin/formTemplates/types/messagesDraft";
import * as State from "src/scenes/orgAdmin/formTemplates/types/messagesState";
import { ListMessageTemplatesFormType } from "./schemas";

export type MessagesInitialValues = {
  listMessages: ListMessageTemplatesFormType;
};

export type Action =
  | {
      type: "UpdateListMessages";
      listMessages: Draft.ListMessagesDraft;
      formTemplateId: uuid;
    }
  | {
      type: "Refresh";
      initialValues: MessagesInitialValues;
      formTemplateId: uuid;
    }
  | { type: "Reset"; formTemplateId: uuid }
  | { type: "ResetComplete" };

export function reducer(state: State.State, action: Action): State.State {
  switch (action.type) {
    case "Reset":
      return reset(state, action.formTemplateId);

    case "ResetComplete":
      return state.set("reset", undefined);

    case "UpdateListMessages":
      return updateListMessages(
        state,
        action.listMessages,
        action.formTemplateId
      );

    case "Refresh":
      return refresh(action.initialValues, action.formTemplateId);

    default:
      const _exhaustiveCheck: never = action;
      return _exhaustiveCheck;
  }
}

function reset(state: State.State, formTemplateId: uuid): State.State {
  const listMessages = state.get("listMessages").map(clearChange);

  return state.set("listMessages", listMessages).set("reset", formTemplateId);
}

function clearChange<T, U>(change: Change<T, U>): Change<T, U> {
  return { ...change, draft: undefined };
}

function updateListMessages(
  state: State.State,
  draft: Draft.ListMessagesDraft,
  formTemplateId: uuid
): State.State {
  const originalListMessagesState = state
    .get("listMessages")
    .get(FormTemplateId(formTemplateId));
  if (!originalListMessagesState) {
    return state;
  }

  const original = originalListMessagesState.original;
  const hasChangesResult = hasChanges(original, draft);

  const updatedState: State.State = state.update(
    "listMessages",
    (listMessages) =>
      listMessages.set(FormTemplateId(formTemplateId), {
        ...originalListMessagesState,
        draft: hasChangesResult ? draft : undefined,
      })
  );

  return updatedState;
}

function toState(
  initialValues: MessagesInitialValues,
  formTemplateId: uuid
): State.State {
  const listMessages: [FormTemplateIdType, State.ListMessagesChange][] = [
    [
      FormTemplateId(formTemplateId),
      { original: initialValues.listMessages, draft: undefined },
    ],
  ];

  return State.NewState({
    listMessages: Immutable.Map(listMessages),
  });
}

function refresh(
  initialValues: MessagesInitialValues,
  formTemplateId: uuid
): State.State {
  return reset(toState(initialValues, formTemplateId), formTemplateId);
}

export function useFormTemplateMessagesProcessor(
  initialValues: MessagesInitialValues,
  formTemplateId: uuid
) {
  const initialState = toState(initialValues, formTemplateId);
  const [state, dispatch] = React.useReducer(reducer, initialState);

  React.useEffect(() => {
    if (state.get("reset")) {
      dispatch({ type: "ResetComplete" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return { state, dispatch };
}
